import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useFormik } from 'formik';
import { FormField } from '../../common/Forms';
import { renderErrors } from '../../common/Forms/utils';
import { requestsActions } from '../../../actions';
import { getValidationSchemaForFields } from '../../../util';

function useMainDataForNomination(nominationId, categories) {
    return useMemo(() => {
        let nomination;
        if (!categories) return {};
        categories.forEach((category) => {
            category.nominations.forEach((nom) => {
                if (nom.id.toString() === nominationId) nomination = nom;
            });
        });
        if (nomination) {
            const schema = getValidationSchemaForFields(nomination.mainFields);
            const initialValues = nomination.mainFields.reduce(
                (obj, field) => ({
                    ...obj,
                    [field.code || field.id]: '',
                }),
                {}
            );
            const nomName = nomination.name || '';
            const nomInfo = nomination.description || '';
            const fieldCodeToId = nomination.mainFields.reduce((obj, field) => {
                if (!field.code) return obj;
                return {
                    ...obj,
                    [field.code]: field.id,
                };
            }, {});
            return { nomination, schema, initialValues, nomName, nomInfo, fieldCodeToId };
        }
        return {};
    }, [nominationId, categories]);
}

function MainFieldsForm(props) {
    const { nomination, formik, errors, submitting, onChange } = props;
    const hasMainFields = nomination && nomination.mainFields.length > 0;
    if (!hasMainFields) return null;
    return (
        <Form onSubmit={formik.handleSubmit} noValidate>
            {nomination.mainFields.map((field) => (
                <Form.Group key={field.id}>
                    <FormField field={field} formik={formik} errors={errors} onChange={onChange} />
                </Form.Group>
            ))}
            {errors && errors.globalError && <Alert variant="danger">{renderErrors(errors.globalError)}</Alert>}

            <Form.Group className="text-center">
                <Button variant="outline-primary" type="submit" className="px-5" disabled={submitting}>
                    {submitting && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
                    <span>Подать</span>
                </Button>
            </Form.Group>
        </Form>
    );
}

function RequestFormPage(props) {
    const { categories, errors, getting, addRequest, clearErrors } = props;
    const { nominationId } = useParams();
    const { nomination, schema, initialValues, nomName, nomInfo, fieldCodeToId } = useMainDataForNomination(
        nominationId,
        categories
    );

    useEffect(() => {
        return () => clearErrors();
    }, [clearErrors]);

    const handleSubmit = useCallback(
        (values) => {
            const castedValues = schema.cast(values);
            const transformedValues = Object.keys(castedValues).reduce((result, key) => {
                if (fieldCodeToId[key]) result[fieldCodeToId[key]] = castedValues[key];
                return result;
            }, {});
            const requestData = {
                nominationId,
                fieldsValue: transformedValues,
            };
            addRequest(requestData);
        },
        [addRequest, nominationId, schema, fieldCodeToId]
    );

    const formik = useFormik({
        initialValues,
        validate: schema && schema.validate,
        onSubmit: handleSubmit,
    });

    const handleChange = useCallback(
        (value, name) => {
            if (errors && errors[name]) clearErrors();
        },
        [errors, clearErrors]
    );

    const submitting = formik.isValidating || getting;

    return (
        <Container className="my-3 my-sm-5">
            {nomination ? (
                <Card style={{ maxWidth: 450 }} className="mx-auto">
                    <Card.Header>
                        <Card.Title>Заявка в секцию {`"${nomName}"`}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Card.Text dangerouslySetInnerHTML={{ __html: nomInfo }} />
                        <MainFieldsForm
                            nomination={nomination}
                            formik={formik}
                            errors={errors}
                            submitting={submitting}
                            onChange={handleChange}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <h2 className="text-center">Нет такой номинации</h2>
            )}
        </Container>
    );
}

function mapStateToProps(state) {
    const {
        categories: { categories },
        requests: { getting, errors },
    } = state;
    return { categories, getting, errors };
}

const actionsToProps = {
    addRequest: requestsActions.add,
    clearErrors: requestsActions.clearErrors,
};

export default connect(mapStateToProps, actionsToProps)(RequestFormPage);
